import React from 'react';
import moment from 'moment';
import classNames from 'classnames';

import { action } from 'mobx';
import { observer } from 'mobx-react';
import { toast } from 'react-hot-toast';
import { useHistory } from 'react-router-dom';

import { Link } from 'ui/base/link';
import { ButtonClickEvent, UnreachableFunction } from 'ui/base/types';
import { Modal, ModalStore } from 'ui/base/modal';
import { ToastID, defaultToast } from 'ui/base/toast';
import { BooleanStore } from 'ui/base/input';
import { useOnScreen } from 'ui/base/screen';
import { BackButton } from 'ui/base/button/back_button';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { StandardPage } from 'ui/base/page/standard_page';

import { ResultKind } from 'services/base/types';
import { BasePath, pagePathToPathString } from 'services/base/page_path';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';

import { HistoryFilter, Incident, IPangolinDomain, Issue, IssueKind, KnownIssue } from 'services/pangolin/types';

import { HostDeviceType } from 'services/pangolin/protos/hosts_pb';

import {
  IssuesService,
} from 'services/pangolin/issues_service';

import {
  hostDisplayDeviceType,
  hostDisplayName,
} from 'services/pangolin/hosts_service';

import {
  getIssueTitle,
  getIssueDescription,
  getIssueKindTitle,
  getIssueKindSubtitle,
  getIssueSeverityLabel,
  getIssueAffected,
  getIssueHistoryFilter,
} from 'services/pangolin/logic/issues/issues';

import {
  themedSeenIconImageStyles,
} from 'assets/enums/seen/seen';

import {
  themedModuleIconImageStyles,
} from 'assets/enums/modules/modules';

import {
  themedDeviceTypeIconImageStyles,
  getDeviceTypeIcon,
} from 'assets/enums/device_types/device_types';

import {
  themedIssueKindIconImageStyles,
  getIssueKindIcon,
} from 'assets/enums/issue_kinds/issue_kinds';

import { MaliciousDomainViewIssueSummaryPartial } from './partials/view_issue_malicious_domain';
import { MaliciousAddressViewIssueSummaryPartial } from './partials/view_issue_malicious_address';
import { MalwareDomainViewIssueSummaryPartial } from './partials/view_issue_malware_domain';
import { MalwareAddressViewIssueSummaryPartial } from './partials/view_issue_malware_address';

import baseStyles from './view_issue.module.css';
import Messages from './view_issue_messages';

type ViewIssueStyles = {
  modal: string;
  container: string;

  header: string;
  headerItem: string;

  hero: string;
  heroIcon: string;
  heroText: string;
  heroTitle: string;
  heroSubtitle: string;

  content: string;
  spacer: string,
  divider: string,
  section: string;
  sectionTitle: string;
  row: string;
  column: string;
  label: string;
  value: string;

  field: string;
  fieldPart: string;
  fieldIcon: string;
  fieldTextLeft: string;
  fieldTextRight: string;
  fieldStats: string;
  fieldStatSeverity: string;
  fieldStatCounter: string;
  fieldInfo: string;
  fieldInfoLong: string;

  modalContainer: string;
  modalContainerPopOut: string;
  modalHeaderRow: string;
  modalContentRow: string;
  modalCloseIcon: string;

  clickable: string;
  disabled: string;

  item: string;
  corpus: string;

  open: string;
};

const themedStyles: ThemedStyles<ViewIssueStyles> = {
  [Theme.DAYLIGHT]: {
    modal: baseStyles.modal,
    container: baseStyles.container,

    header: baseStyles.header,
    headerItem: baseStyles.headerItem,

    hero: baseStyles.hero,
    heroIcon: baseStyles.heroIcon,
    heroText: baseStyles.heroText,
    heroTitle: baseStyles.heroTitle,
    heroSubtitle: baseStyles.heroSubtitle,

    content: baseStyles.content,
    spacer: baseStyles.spacer,
    divider: baseStyles.daylightDivider,
    section: baseStyles.section,
    sectionTitle: baseStyles.daylightSectionTitle,
    row: baseStyles.row,
    column: baseStyles.column,
    label: baseStyles.daylightLabel,
    value: baseStyles.value,

    field: baseStyles.field,
    fieldPart: baseStyles.fieldPart,
    fieldIcon: baseStyles.fieldIcon,
    fieldTextLeft: baseStyles.fieldTextLeft,
    fieldTextRight: baseStyles.fieldTextRight,
    fieldStats: baseStyles.fieldStats,
    fieldStatSeverity: baseStyles.fieldStatSeverity,
    fieldStatCounter: baseStyles.fieldStatCounter,
    fieldInfo: baseStyles.fieldInfo,
    fieldInfoLong: baseStyles.fieldInfoLong,

    modalContainer: baseStyles.modalContainer,
    modalContainerPopOut: baseStyles.modalContainerPopOut,
    modalHeaderRow: baseStyles.modalHeaderRow,
    modalContentRow: baseStyles.modalContentRow,
    modalCloseIcon: baseStyles.daylightModalCloseIcon,

    clickable: baseStyles.clickable,
    disabled: baseStyles.disabled,

    item: baseStyles.daylightItem,
    corpus: baseStyles.daylightCorpus,

    open: baseStyles.daylightOpen,
  },
  [Theme.MIDNIGHT]: {
    modal: baseStyles.modal,
    container: baseStyles.container,

    header: baseStyles.header,
    headerItem: baseStyles.headerItem,

    hero: baseStyles.hero,
    heroIcon: baseStyles.heroIcon,
    heroText: baseStyles.heroText,
    heroTitle: baseStyles.heroTitle,
    heroSubtitle: baseStyles.heroSubtitle,

    content: baseStyles.content,
    spacer: baseStyles.spacer,
    divider: baseStyles.midnightDivider,
    section: baseStyles.section,
    sectionTitle: baseStyles.midnightSectionTitle,
    row: baseStyles.row,
    column: baseStyles.column,
    label: baseStyles.midnightLabel,
    value: baseStyles.value,

    field: baseStyles.field,
    fieldPart: baseStyles.fieldPart,
    fieldIcon: baseStyles.fieldIcon,
    fieldTextLeft: baseStyles.fieldTextLeft,
    fieldTextRight: baseStyles.fieldTextRight,
    fieldStats: baseStyles.fieldStats,
    fieldStatSeverity: baseStyles.fieldStatSeverity,
    fieldStatCounter: baseStyles.fieldStatCounter,
    fieldInfo: baseStyles.fieldInfo,
    fieldInfoLong: baseStyles.fieldInfoLong,

    modalContainer: baseStyles.modalContainer,
    modalContainerPopOut: baseStyles.modalContainerPopOut,
    modalHeaderRow: baseStyles.modalHeaderRow,
    modalContentRow: baseStyles.modalContentRow,
    modalCloseIcon: baseStyles.midnightModalCloseIcon,

    clickable: baseStyles.clickable,
    disabled: baseStyles.disabled,

    item: baseStyles.midnightItem,
    corpus: baseStyles.midnightCorpus,

    open: baseStyles.midnightOpen,
  },
};

type ViewIssueHeroProps = {
  issue: Issue;
};
const ViewIssueHero = observer(({
  issue,
}: ViewIssueHeroProps) => {
  const styles = useStyles(themedStyles);
  const issueIconImages = useStyles(themedIssueKindIconImageStyles);
  return (
    <div className={styles.hero}>
      <img className={styles.heroIcon} alt='Issue Icon'
           src={getIssueKindIcon(issueIconImages, issue.kind)} />
      <div className={styles.heroText}>
        <div className={styles.heroTitle}>
          {getIssueKindTitle(issue.kind)}
        </div>
        <div className={styles.heroSubtitle}>
          {getIssueKindSubtitle(issue.kind)}
        </div>
      </div>
    </div>
  );
});

type ViewIssueSummaryPartialProps = {
  domain: IPangolinDomain;
  issue: KnownIssue;
};
const ViewIssueSummaryPartial = observer(({ domain, issue }: ViewIssueSummaryPartialProps) => {
  switch(issue.kind) {
    case IssueKind.MALICIOUS_DOMAIN:
      return (
        <MaliciousDomainViewIssueSummaryPartial
          domain={domain}
          issue={issue}
        />
      );
    case IssueKind.MALICIOUS_ADDRESS:
      return (
        <MaliciousAddressViewIssueSummaryPartial
          domain={domain}
          issue={issue}
        />
      );
    case IssueKind.MALWARE_DOMAIN:
      return (
        <MalwareDomainViewIssueSummaryPartial
          domain={domain}
          issue={issue}
        />
      );
    case IssueKind.MALWARE_ADDRESS:
      return (
        <MalwareAddressViewIssueSummaryPartial
          domain={domain}
          issue={issue}
        />
      );
    default:
      return UnreachableFunction(issue);
  }
});

type ViewIssueSummaryProps = {
  domain: IPangolinDomain;
  issue: KnownIssue;
};
const ViewIssueSummary = observer(({ domain, issue }: ViewIssueSummaryProps) => {
  const styles = useStyles(themedStyles);

  const summary = getIssueTitle(domain, issue);
  const description = getIssueDescription(domain, issue);

  return (
    <div className={styles.section}>
      <ViewIssueSummaryPartial
        domain={domain}
        issue={issue}
      />
      <div className={styles.field}>
        <div className={styles.fieldStats}>
          <div className={styles.fieldStatSeverity}>
            {getIssueSeverityLabel(issue.severity)}
            <div className={styles.fieldInfo}>
              {Messages.statSeverity()}
            </div>
          </div>
        </div>
        <div className={styles.fieldStats}>
          <div className={styles.fieldStatCounter}>
            {getIssueAffected(issue).length}
            <div className={styles.fieldInfo}>
              {Messages.statAffected()}
            </div>
          </div>
          <div className={styles.fieldStatCounter}>
            {issue.incidents.size}
            <div className={styles.fieldInfo}>
              {Messages.statIncidents()}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={classNames(styles.column, styles.corpus)}>
          <div className={styles.label}>
            {Messages.aboutSummary()}
          </div>
          <div className={styles.value}>
            {summary}
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={classNames(styles.column, styles.corpus)}>
          <div className={styles.label}>
            {Messages.aboutDescription()}
          </div>
          <div className={styles.value}>
            {description}
          </div>
        </div>
      </div>
    </div>
  );
});

type ViewIssueHostModalProps = {
  domain: IPangolinDomain;
  issue: KnownIssue;
  mac: string;
  store: ModalStore;
};

export const ViewIssueHostModal = observer(({ domain, issue, mac, store }: ViewIssueHostModalProps) => {
  const history = useHistory();

  const styles = useStyles(themedStyles);
  const moduleIconImages = useStyles(themedModuleIconImageStyles);

  const host_host = domain.hosts.getMac(mac);

  const incident_last = issue.incidents.getAt(0);

  const filter_object = incident_last && host_host ? (() => {
    const filter_last = new Date(incident_last.timestamp);
    filter_last.setMinutes(filter_last.getMinutes() + 1);

    const filter_first = new Date(incident_last.timestamp);
    filter_first.setMinutes(filter_first.getMinutes() - 1);

    return { end: filter_last, start: filter_first,
             devices: [host_host.id] };
  })() : undefined;

  const onModalClose = React.useCallback((event: ButtonClickEvent) => {
    event.preventDefault();
    store.hideModal();
  }, [store]);

  const onViewHistory = React.useCallback((event: ButtonClickEvent, filter: Partial<HistoryFilter>) => {
    event.preventDefault();
    domain.historyFilter = {
      ...domain.historyFilter,
      ...filter,
    };
    domain.historyFiltered = true;
    history.push(pagePathToPathString({
      kind: BasePath.HISTORY_LIST,
      pangolinID: domain.id,
    }));
  }, [domain, history]);

  return (
    <Modal store={store}>
      <div className={classNames(styles.modalContainer, { [styles.modalContainerPopOut]: store.visible })}>
        <StandardPage>
          <div className={styles.modalHeaderRow}>
            {Messages.modalHostTitle()}
            <button
              className={styles.modalCloseIcon}
              onClick={onModalClose}
            />
          </div>
          <div className={styles.modalContentRow}>
            {
              host_host ? (
                <Link className={classNames(styles.field, styles.item, styles.clickable)}
                      path={{ kind: BasePath.DEVICES_VIEW, pangolinID: domain.id,
                              deviceID: host_host.id }}>
                  <img className={styles.fieldIcon} src={moduleIconImages.devices} alt='Device Icon' />
                  <div className={styles.fieldTextRight}>
                    {Messages.modalHostLinkDeviceTitle()}
                    <div className={styles.fieldInfoLong}>
                      {Messages.modalHostLinkDeviceLabel()}
                    </div>
                  </div>
                  <div className={styles.open}/>
                </Link>
              ) : (
                <div className={classNames(styles.field, styles.item, styles.disabled)}>
                  <img className={styles.fieldIcon} src={moduleIconImages.devices} alt='Device Icon' />
                  <div className={styles.fieldTextRight}>
                    {Messages.modalHostLinkDeviceTitle()}
                    <div className={styles.fieldInfoLong}>
                      {Messages.modalHostLinkDeviceLabel()}
                    </div>
                  </div>
                  <div className={styles.open}/>
                </div>
              )
            }
            {
              filter_object ? (
                <button className={classNames(styles.field, styles.item, styles.clickable)}
                        type='button' onClick={(event) => onViewHistory(event, filter_object)}>
                  <img className={styles.fieldIcon} src={moduleIconImages.history} alt='History Icon' />
                  <div className={styles.fieldTextRight}>
                    {Messages.modalHostLinkTrafficTitle()}
                    <div className={styles.fieldInfoLong}>
                      {Messages.modalHostLinkTrafficLabel()}
                    </div>
                  </div>
                  <div className={styles.open}/>
                </button>
              ) : (
                <div className={classNames(styles.field, styles.item, styles.disabled)}>
                  <img className={styles.fieldIcon} src={moduleIconImages.history} alt='History Icon' />
                  <div className={styles.fieldTextRight}>
                    {Messages.modalHostLinkTrafficTitle()}
                    <div className={styles.fieldInfoLong}>
                      {Messages.modalHostLinkTrafficLabel()}
                    </div>
                  </div>
                  <div className={styles.open}/>
                </div>
              )
            }
          </div>
        </StandardPage>
      </div>
    </Modal>
  );
});

type ViewIssueHostProps = {
  domain: IPangolinDomain;
  issue: KnownIssue;
  mac: string;
};
const ViewIssueHost = observer(({
  domain,
  issue,
  mac,
}: ViewIssueHostProps) => {
  const styles = useStyles(themedStyles);
  const deviceIconImages = useStyles(themedDeviceTypeIconImageStyles);

  const host = domain.hosts.getMac(mac);
  const host_type = host ? hostDisplayDeviceType(host) : HostDeviceType.H_UNKNOWN;
  const host_name = host ? (hostDisplayName(host) ?? host.mac ?? host.ip) : mac;
  const host_icon = getDeviceTypeIcon(deviceIconImages, host_type);

  const [show] = React.useState(new ModalStore(false));
  React.useEffect(show.hideOnBackEffect);

  const onShowModal = React.useCallback(action(() => {
    show.showModal();
  }), [show]);

  return (
    <>
      <ViewIssueHostModal
        domain={domain}
        issue={issue}
        mac={mac}
        store={show}
      />
      <div className={classNames(styles.field, styles.item, styles.clickable)} onClick={onShowModal}>
        <img className={styles.fieldIcon} src={host_icon} alt='Host Icon' />
        <div className={styles.fieldTextRight}>
          {host_name}
          <div className={styles.fieldInfo}>
            {host ? host.ip ? host.ip : host.mac : Messages.sectionAffectedEphmeral()}
          </div>
        </div>
        <div className={styles.open}/>
      </div>
    </>
  );
});

type ViewIssueHostsProps = {
  domain: IPangolinDomain;
  issue: KnownIssue;
};
const ViewIssueHosts = observer(({ domain, issue }: ViewIssueHostsProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        {Messages.sectionAffected()}
      </div>
      <div className={styles.field}>
        <div className={styles.fieldPart}>
          {
            getIssueAffected(issue).map((mac) =>
              <ViewIssueHost
                key={mac}
                domain={domain}
                issue={issue}
                mac={mac}
              />
            )
          }
        </div>
      </div>
    </div>
  );
});

type ViewIssueIncidentModalProps = {
  domain: IPangolinDomain;
  issue: KnownIssue;
  incident: Incident;
  store: ModalStore;
};
export const ViewIssueIncidentModal = observer(({ domain, issue, incident, store }: ViewIssueIncidentModalProps) => {
  const history = useHistory();

  const styles = useStyles(themedStyles);
  const seenIconImages = useStyles(themedSeenIconImageStyles);
  const moduleIconImages = useStyles(themedModuleIconImageStyles);

  const filter_last = new Date(incident.timestamp);
  filter_last.setMinutes(filter_last.getMinutes() + 1);

  const filter_first = new Date(incident.timestamp);
  filter_first.setMinutes(filter_first.getMinutes() - 1);

  const filter_object = getIssueHistoryFilter(domain, issue, incident);

  const onModalClose = React.useCallback((event: ButtonClickEvent) => {
    event.preventDefault();
    store.hideModal();
  }, [store]);

  const onMarkAsUnseen = React.useCallback((event: ButtonClickEvent) => {
    event.preventDefault();
    if (incident.seen) {
      IssuesService.markSeen(domain, issue, {
        uuid: incident.uuid, seen: false,
      }).then(result => {
        if (result.kind === ResultKind.OK) {
          toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
          toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
          toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
        } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
          defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
        } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
          defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
        } else {
          defaultToast(ToastID.ERROR_ACTION_GENERIC);
        }
      });
    }
    store.hideModal();
  }, [store, domain, issue, incident]);

  const onViewHistory = React.useCallback((event: ButtonClickEvent, filter: Partial<HistoryFilter>) => {
    event.preventDefault();
    domain.historyFilter = {
      ...domain.historyFilter,
      ...filter,
    };
    domain.historyFiltered = true;
    history.push(pagePathToPathString({
      kind: BasePath.HISTORY_LIST,
      pangolinID: domain.id,
    }));
  }, [domain, history]);

  return (
    <Modal store={store}>
      <div className={classNames(styles.modalContainer, { [styles.modalContainerPopOut]: store.visible })}>
        <StandardPage>
          <div className={styles.modalHeaderRow}>
            {Messages.modalIncidentTitle()}
            <button
              className={styles.modalCloseIcon}
              onClick={onModalClose}
            />
          </div>
          <div className={styles.modalContentRow}>
            <button className={classNames(styles.field, styles.item, styles.clickable)}
                    type='button' onClick={onMarkAsUnseen}>
              <img className={styles.fieldIcon} src={seenIconImages.unseen} alt='Unseen Icon' />
              <div className={styles.fieldTextRight}>
                {Messages.modalIncidentLinkUnseenTitle()}
                <div className={styles.fieldInfoLong}>
                  {Messages.modalIncidentLinkUnseenLabel()}
                </div>
              </div>
              <div className={styles.open}/>
            </button>
            {
              filter_object ? (
                <button className={classNames(styles.field, styles.item, styles.clickable)}
                      type='button' onClick={(event) => onViewHistory(event, filter_object)}>
                  <img className={styles.fieldIcon} src={moduleIconImages.history} alt='History Icon' />
                  <div className={styles.fieldTextRight}>
                    {Messages.modalIncidentLinkTrafficTitle()}
                    <div className={styles.fieldInfoLong}>
                      {Messages.modalIncidentLinkTrafficLabel()}
                    </div>
                  </div>
                  <div className={styles.open}/>
                </button>
              ) : (
                <div className={classNames(styles.field, styles.item, styles.disabled)}>
                  <img className={styles.fieldIcon} src={moduleIconImages.history} alt='History Icon' />
                  <div className={styles.fieldTextRight}>
                    {Messages.modalIncidentLinkTrafficTitle()}
                    <div className={styles.fieldInfoLong}>
                      {Messages.modalIncidentLinkTrafficLabel()}
                    </div>
                  </div>
                  <div className={styles.open}/>
                </div>
              )
            }
          </div>
        </StandardPage>
      </div>
    </Modal>
  );
});

type ViewIssueIncidentProps = {
  domain: IPangolinDomain;
  issue: KnownIssue;
  incident: Incident;
  locale: string;
  format: object;
};
const ViewIssueIncident = observer(({
  domain,
  issue,
  incident,
  locale,
  format,
}: ViewIssueIncidentProps) => {
  const ref = React.useRef<HTMLDivElement>(null);

  const visible = useOnScreen(ref);
  const styles = useStyles(themedStyles);
  const seenIconImages = useStyles(themedSeenIconImageStyles);

  const incident_date = incident.timestamp;
  const incident_icon = incident.seen ? seenIconImages.seen : seenIconImages.unseen;

  const [show] = React.useState(new ModalStore(false));
  React.useEffect(show.hideOnBackEffect);

  const onShowModal = React.useCallback(action(() => {
    show.showModal();
  }), [show]);

  const [marked] = React.useState(new BooleanStore(false));

  React.useEffect(() => {
    if (incident != null && !incident.seen && visible && !marked.value) {
      marked.setValue(true);
      IssuesService.markSeen(domain, issue, {
        uuid: incident.uuid, seen: true,
      });
    }
  }, [marked, visible, domain, issue, incident]);

  return (
    <>
      <ViewIssueIncidentModal
        domain={domain}
        issue={issue}
        incident={incident}
        store={show}
      />
      <div className={classNames(styles.field, styles.item, styles.clickable)} onClick={onShowModal} ref={ref}>
        <img className={styles.fieldIcon} src={incident_icon} alt='Incident Icon' />
        <div className={styles.fieldTextRight}>
          {moment(incident_date).fromNow()}
          <div className={styles.fieldInfo}>
            {incident_date.toLocaleString(locale, format)}
          </div>
        </div>
        <div className={styles.open}/>
      </div>
    </>
  );
});

type ViewIssueIncidentsProps = {
  domain: IPangolinDomain;
  issue: KnownIssue;
};
const ViewIssueIncidents = observer(({ domain, issue }: ViewIssueIncidentsProps) => {
  const styles = useStyles(themedStyles);

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        {Messages.sectionIncidents()}
      </div>
      <div className={styles.field}>
        <div className={styles.fieldPart}>
          {
            issue.incidents.map((incident) =>
              <ViewIssueIncident
                key={incident.uuid}
                domain={domain}
                issue={issue}
                incident={incident}
                locale={locale}
                format={format}
              />
            )
          }
        </div>
      </div>
    </div>
  );
});

type ViewIssueMainProps = {
  domain: IPangolinDomain;
  issue: Issue;
};
const ViewIssueMain = observer(({
  domain,
  issue,
}: ViewIssueMainProps) => {
  const ref = React.useRef<HTMLDivElement>(null);

  const history = useHistory();
  const visible = useOnScreen(ref);
  const styles = useStyles(themedStyles);

  const incident = issue.incidents.getAt(0);

  const [marked] = React.useState(new BooleanStore(false));

  const onHideModal = React.useCallback(action(() => {
    domain.issuesItem = undefined;
    history.goBack();
  }), []);

  React.useEffect(() => {
    if (incident != null && !incident.seen && visible && !marked.value) {
      marked.setValue(true);
      IssuesService.markSeen(domain, issue, {
        uuid: incident.uuid, seen: true,
      });
    }
  }, [marked, visible, domain, issue, incident]);

  return (
    <div className={styles.container} ref={ref}>
      <div className={styles.header}>
        <div className={styles.headerItem}>
          <BackButton onClick={onHideModal}/>
        </div>
      </div>
      <ViewIssueHero issue={issue} />
      <div className={styles.content}>
        {issue.kind !== IssueKind.UNKNOWN ? <>
          <ViewIssueSummary
            domain={domain}
            issue={issue}
          />
          <ViewIssueHosts
            domain={domain}
            issue={issue}
          />
          <ViewIssueIncidents
            domain={domain}
            issue={issue}
          />
        </>: null}
      </div>
    </div>
  );
});

type BaseViewIssueProps = {
  domain: IPangolinDomain;
};
export const BaseViewIssue = observer(({
  domain,
}: BaseViewIssueProps) => {
  const issue = domain.issuesItem;
  return (
    <StandardPage>
      {issue && (
        <ViewIssueMain
          domain={domain}
          issue={issue}
        />
      )}
    </StandardPage>
  );
});

type ViewIssueModalProps = {
  domain: IPangolinDomain;
  modal: ModalStore;
};

export const ViewIssueModal = observer(({
  domain,
  modal,
}: ViewIssueModalProps) => {
  const styles = useStyles(themedStyles);
  return (
    <Modal store={modal}>
      <div className={styles.modal}>
        {
          modal.visible &&
          <BaseViewIssue
            domain={domain}
          />
        }
      </div>
    </Modal>
  );
});
